/**
 * Tenants (organizaciones) CRUD UI — solo superadmin de plataforma.
 * Permite ver todas las organizaciones dadas de alta en la instancia
 * compartida y hacer el onboarding de una nueva (tenant + primer admin),
 * reemplazando los pasos manuales por SQL descritos en MULTITENANT_PLAN.md.
 *
 * Data/CRUD plumbing lives in `useCrudWithMembers` (shared with
 * TeamsManagementPanel) — this file only owns the fields and markup that
 * are actually specific to tenants (slug, admin_email, role per member).
 */
import React, { useState, useMemo } from 'react';
import {
  Typography, Button, Table, Space, Tag, Popconfirm,
  message, Spin, Empty, Modal, Form, Input, Select, Drawer, List,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ApartmentOutlined, UserAddOutlined } from '@ant-design/icons';
import { EXECUTOR_API } from '../../types';
import { useCrudWithMembers } from '../../hooks/useCrudWithMembers';

const { Text } = Typography;

type TenantRole = 'admin' | 'editor' | 'viewer';

interface TenantSummary {
  slug: string;
  name: string;
  members_count: number;
  created_at: string;
}

interface TenantMember {
  email: string;
  role: TenantRole;
  created_at: string;
}

interface TenantDetail extends TenantSummary {
  members: TenantMember[];
}

const ROLE_OPTIONS: { value: TenantRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const OrganizationsManagementPanel: React.FC = () => {
  const config = useMemo(
    () => ({
      basePath: `${EXECUTOR_API}/agent/builder/tenants`,
      listField: 'tenants',
      getKey: (t: TenantSummary) => t.slug,
      errorMessages: {
        list: 'Error al cargar organizaciones',
        create: 'Error al crear la organización',
        update: 'Error al actualizar',
        delete: 'Error al eliminar',
        addMember: 'No se pudo añadir el miembro',
      },
    }),
    []
  );
  const {
    items: tenants, loading, create, fetchDetail, update, remove, addMember, removeMember,
  } = useCrudWithMembers<TenantSummary, TenantDetail>(config);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<TenantRole>('viewer');
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const handleCreate = async (values: { slug: string; name: string; admin_email: string }) => {
    setCreating(true);
    const created = await create(values);
    setCreating(false);
    if (created) {
      message.success(`Organización "${values.name}" creada`);
      setCreateOpen(false);
      form.resetFields();
    }
  };

  const openEdit = async (tenant: TenantSummary) => {
    setDrawerLoading(true);
    // No placeholder with an empty member list: if the detail fetch fails, the
    // drawer must not open showing "Sin miembros" for an organization that has some.
    const detail = await fetchDetail(tenant.slug);
    if (detail) {
      setEditingTenant(detail);
      editForm.setFieldsValue({ name: detail.name });
    } else {
      message.error('Error al cargar la organización');
      setEditingTenant(null);
    }
    setDrawerLoading(false);
  };

  const handleUpdateDetails = async (values: { name: string }) => {
    if (!editingTenant) return;
    const ok = await update(editingTenant.slug, values);
    if (ok) {
      message.success('Organización actualizada');
      setEditingTenant({ ...editingTenant, name: values.name });
    }
  };

  const handleDelete = async (tenant: TenantSummary) => {
    const ok = await remove(tenant);
    if (ok) message.success(`Organización "${tenant.name}" eliminada`);
  };

  const handleAddMember = async () => {
    if (!editingTenant || !memberEmail.trim()) return;
    setAddingMember(true);
    const email = memberEmail.trim().toLowerCase();
    const ok = await addMember(editingTenant.slug, { email, role: memberRole });
    if (ok) {
      setEditingTenant({
        ...editingTenant,
        members: [...editingTenant.members, { email, role: memberRole, created_at: new Date().toISOString() }]
          .sort((a, b) => a.email.localeCompare(b.email)),
      });
      setMemberEmail('');
      setMemberRole('viewer');
    }
    setAddingMember(false);
  };

  const handleRemoveMember = async (email: string) => {
    if (!editingTenant) return;
    const ok = await removeMember(editingTenant.slug, email);
    if (ok) {
      setEditingTenant({
        ...editingTenant,
        members: editingTenant.members.filter((m) => m.email !== email),
      });
    }
  };

  const columns = [
    {
      title: 'Organización', dataIndex: 'name', key: 'name',
      render: (name: string, record: TenantSummary) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.slug}</Text>
        </Space>
      ),
    },
    {
      title: 'Miembros', dataIndex: 'members_count', key: 'members_count', width: 120,
      render: (n: number) => <Tag icon={<ApartmentOutlined />}>{n}</Tag>,
    },
    {
      title: 'Creada', dataIndex: 'created_at', key: 'created_at', width: 140,
      render: (d: string) => new Date(d).toLocaleDateString(),
    },
    {
      title: '', key: 'actions', width: 100,
      render: (_: unknown, record: TenantSummary) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="¿Eliminar esta organización?"
            description="Solo se puede eliminar una organización sin recursos (agentes, flujos, ejecuciones...); si aún los tiene, se te indicará. Se eliminan sus membresías. Esta acción no se puede deshacer."
            onConfirm={() => handleDelete(record)}
            okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary" style={{ flex: 1 }}>
          Organizaciones (clientes) dadas de alta en esta instancia. Cada una tiene sus propios
          usuarios, agentes y Brain, aislados del resto.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Nueva organización
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : tenants.length === 0 ? (
        <Empty description="Aún no hay organizaciones">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Crear la primera
          </Button>
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={tenants.map((t) => ({ ...t, key: t.slug }))}
          pagination={false} size="middle"
        />
      )}

      {/* Onboarding: nueva organización */}
      <Modal
        title="Nueva organización"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, max: 200 }]}>
            <Input placeholder="Ej: Propfolio" autoFocus />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug"
            extra="Identificador estable, usado internamente. No se puede cambiar después."
            rules={[
              { required: true, max: 100 },
              { pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/, message: 'Minúsculas, números y guiones (ej. "propfolio")' },
            ]}
          >
            <Input placeholder="propfolio" />
          </Form.Item>
          <Form.Item
            name="admin_email"
            label="Email del primer admin"
            extra="Esta persona podrá gestionar usuarios y recursos de la organización desde el primer día."
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder="admin@propfolio.com" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateOpen(false); form.resetFields(); }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={creating}>Crear organización</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit + manage members drawer */}
      <Drawer
        title={editingTenant ? `Organización: ${editingTenant.name}` : ''}
        open={!!editingTenant}
        onClose={() => setEditingTenant(null)}
        width={520}
        destroyOnClose
      >
        {drawerLoading || !editingTenant ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <>
            <Form form={editForm} layout="vertical" onFinish={handleUpdateDetails}>
              <Form.Item name="name" label="Nombre" rules={[{ required: true, max: 200 }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Slug">
                <Input value={editingTenant.slug} disabled />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">Guardar cambios</Button>
              </Form.Item>
            </Form>

            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <Text strong style={{ fontSize: 14 }}>Miembros ({editingTenant.members.length})</Text>
            </div>

            <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
              <Input
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                placeholder="email@organizacion.com"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddMember(); }}
              />
              <Select<TenantRole>
                value={memberRole}
                onChange={setMemberRole}
                options={ROLE_OPTIONS}
                style={{ width: 110 }}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                loading={addingMember}
                onClick={handleAddMember}
                disabled={!memberEmail.trim()}
              >
                Añadir
              </Button>
            </Space.Compact>

            {editingTenant.members.length === 0 ? (
              <Empty description="Sin miembros todavía" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                bordered
                dataSource={editingTenant.members}
                renderItem={(member) => (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key="remove"
                        title="¿Quitar este miembro de la organización?"
                        onConfirm={() => handleRemoveMember(member.email)}
                        okText="Quitar" cancelText="Cancelar"
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>,
                    ]}
                  >
                    <Space>
                      <Text>{member.email}</Text>
                      <Tag>{member.role}</Tag>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default OrganizationsManagementPanel;
